import { WebGLRenderer } from "three";

import { Context } from "../context";
import { CameraMode, CameraLandmark } from "../camera/controller";
import { PoserApp } from "../poser";
import { PoserClock } from "../util/clock";
import { UserOptionPreset, UserOptions } from "../util/user-options";
import { TeleportPersonalControllerStrategy } from "../camera/strategy/teleport/personal";

var previousContainerStyle: Partial<CSSStyleDeclaration> | undefined =
  undefined;

export type PublicCameraOption =
  | "action"
  | "jumbotron"
  | "batter"
  | "pitcher"
  | "free"
  | "strikezone";
export interface PublicCameraConfig {
  type: PublicCameraOption;
  key: string;
  title: string;
}

export const QUALITY_CONFIG = [
  { label: "NORMAL", value: "low" as UserOptionPreset },
  { label: "HIGH", value: "high" as UserOptionPreset },
];

export const SPEED_CONFIG = [
  { label: "1x", value: 1 },
  { label: "1/2x", value: 0.5 },
  { label: "1/4x", value: 0.25 },
];

export const CAMERA_CONFIGS: PublicCameraConfig[] = [
  {
    type: "action",
    title: "Action",
    key: "fieldvision_camera_fieldvision",
  },
  {
    type: "free",
    title: "Fan Control",
    key: "fieldvision_camera_fan",
  },
  {
    type: "batter",
    title: "Batter",
    key: "fieldvision_camera_batter",
  },
  {
    type: "pitcher",
    title: "Pitcher",
    key: "fieldvision_camera_pitcher",
  },
  {
    type: "strikezone",
    title: "Strikezone",
    key: "fieldvision_camera_strikezone",
  },
  {
    type: "jumbotron",
    title: "Jumbotron",
    key: "fieldvision_camera_jumbo",
  },
];

const CAMERA_CONFIGS_MAP = new Map<
  PublicCameraOption,
  { index: number; config: PublicCameraConfig }
>();
for (let index = 0; index < CAMERA_CONFIGS.length; index++) {
  const config = CAMERA_CONFIGS[index];
  CAMERA_CONFIGS_MAP.set(config.type, { index, config });
}

const cameraModeToPublic: Map<CameraMode, PublicCameraOption> = new Map([
  [CameraMode.ACTION, "action"],
  [CameraMode.JUMBOTRON, "jumbotron"],
  [CameraMode.BATTER, "batter"],
  [CameraMode.PITCHER, "pitcher"],
  [CameraMode.MAP, "free"],
  [CameraMode.STRIKEZONE, "strikezone"],
]);

export const cameraOptionDetails: {
  [key in PublicCameraOption]: {
    mode: CameraMode;
    landmarks: CameraLandmark[];
  };
} = {
  action: {
    mode: CameraMode.ACTION,
    landmarks: [CameraLandmark.HH],
  },
  batter: {
    mode: CameraMode.BATTER,
    landmarks: [CameraLandmark.HH],
  },
  pitcher: {
    mode: CameraMode.PITCHER,
    landmarks: [CameraLandmark.MOUND_CENTER],
  },
  free: {
    mode: CameraMode.MAP,
    landmarks: [CameraLandmark.HH],
  },
  strikezone: {
    mode: CameraMode.STRIKEZONE,
    landmarks: [
      CameraLandmark.STRIKEZONE_CENTER,
      CameraLandmark.STRIKEZONE_CENTER_MOBILE,
    ],
  },
  jumbotron: {
    mode: CameraMode.JUMBOTRON,
    landmarks: [CameraLandmark.HH],
  },
};

export class FieldVisionController {
  private app: PoserApp;
  private context: Context;

  publicCameraMode?: PublicCameraOption;

  private cameraModeSubscribers = new Map<
    string,
    (mode: PublicCameraConfig) => void
  >();

  constructor(app: PoserApp) {
    this.app = app;
    this.context = app.context;

    // monkey patch camera change callback
    const cameraCb = this.context.callbacks.onCameraChange;
    this.context.callbacks.onCameraChange = (e) => {
      if (e.options?.fromInteraction && !e.options.fromManager) {
        if (cameraModeToPublic.get(e.mode)) {
          this.publicCameraMode = cameraModeToPublic.get(e.mode);
          const config = this.publicCameraMode
            ? CAMERA_CONFIGS_MAP.get(this.publicCameraMode)
            : undefined;
          if (config) {
            for (const cb of this.cameraModeSubscribers.values()) {
              cb(config.config);
            }
          }
        }
      }

      cameraCb(e);
    };
  }

  get debugHandle(): { app: PoserApp; context: Context } {
    console.warn(`exposing controller innards`);
    return { app: this.app, context: this.context };
  }

  get renderer(): WebGLRenderer {
    return this.app.context.renderer;
  }

  get clock(): PoserClock {
    return this.app.context.clock;
  }

  get userOption(): UserOptions {
    return this.context.userOptions;
  }

  get positions(): number[] {
    const positions: number[] = [];
    const actors = this.app.context.world?.actors;
    if (actors) {
      for (const actor of actors.getActors()) {
        positions.push(actor.positionId);
      }
    }
    return positions;
  }

  get cameraController() {
    return this.context.cameraController;
  }

  get cameraConfigs() {
    return CAMERA_CONFIGS;
  }

  get isPlaying(): boolean {
    return this.context.state.playing === true;
  }

  get playCurrentTime(): number {
    return this.context.clock.real - this.playStartTime;
  }

  set playCurrentTime(offset: number) {
    this.app.seekTo(
      Math.min(
        this.playEndTime,
        Math.max(this.playStartTime, this.playStartTime + offset)
      )
    );
  }

  get playStartTime(): number {
    return this.context.clock.playLimits ? this.context.clock.playLimits[0] : 0;
  }

  get playEndTime(): number {
    return this.context.clock.playLimits ? this.context.clock.playLimits[1] : 0;
  }

  get playTotalTime(): number {
    return this.context.clock.playPeriod ? this.context.clock.playPeriod : 0;
  }

  get assetBase(): string | undefined {
    return this.context.options.assetBase;
  }

  play(): void {
    this.context.state.playing = true;
  }

  pause(): void {
    this.context.state.playing = false;
  }

  rewind(): void {
    this.playCurrentTime -= 10;
  }

  fastForward(): void {
    this.playCurrentTime += 10;
  }

  toggleLoop(): void {
    this.context.clock.loop = !this.context.clock.loop;
  }

  seekToAbsolute(realSeconds: number): void {
    this.app.seekTo(realSeconds);
  }

  seekToRelative(seconds: number): void {
    const t0 = this.context.clock.playLimits
      ? this.context.clock.playLimits[0]
      : this.context.clock.timelineLimits[0];
    this.app.seekTo(t0 + seconds);
  }

  setCameraMode(cameraOption: PublicCameraOption): void {
    if (!this.cameraController) return;

    const { mode, landmarks } = cameraOptionDetails[cameraOption];

    const width = this.context.container?.clientWidth;
    const height = this.context.container?.clientHeight;
    const isMobile = height && width && height > width;

    const landmark =
      landmarks.length > 1 && isMobile ? landmarks[1] : landmarks[0];

    // mobile position different
    // if (true) {
    // if (height && width && height > width) {
    const initialized = this.publicCameraMode !== undefined;

    if (!initialized) {
      // initialize
      this.cameraController.addAuxStrategy(TeleportPersonalControllerStrategy);

      this.cameraController.setMode(CameraMode.INIT, {
        fromManager: false,
        initData: {
          landmark,
          mode,
        },
        fromInteraction: true,
      });
    } else {
      // normal operation
      // NB: landmark only supported by MAP mode currently
      this.cameraController.setMode(mode, {
        landmark,
        fromInteraction: true,
      });
    }
  }

  nextCameraMode() {
    const config = this.publicCameraMode
      ? CAMERA_CONFIGS_MAP.get(this.publicCameraMode)
      : undefined;
    if (!config) {
      console.error("camera mode uninitialized");
      return;
    }
    let nextIndex = (config.index + 1) % CAMERA_CONFIGS.length;
    if (CAMERA_CONFIGS[nextIndex].type === "jumbotron") {
      // don't allow user to user to select jumbotron
      nextIndex = (nextIndex + 1) % CAMERA_CONFIGS.length;
    }
    this.setCameraMode(CAMERA_CONFIGS[nextIndex].type);
  }

  setPlaybackSpeed = (speedValue: number) => {
    this.clock.speed = speedValue;
  };

  setVisualQualityPreset = (quality: UserOptionPreset) => {
    this.userOption.loadPreset(quality);
    this.userOption.save();
    this.cleanUp(true);
    this.init();
  };

  enterFullscreen(): void {
    if (this.context.container) {
      const container = this.context.container;
      previousContainerStyle = {
        width: container.style.width,
        height: container.style.height,
        position: container.style.position,
        zIndex: container.style.zIndex,
        top: container.style.top,
        left: container.style.left,
      };
      container.style.width = "100%";
      container.style.height = "100%";
      container.style.position = "fixed";
      container.style.zIndex = "100";
      container.style.top = "0";
      container.style.left = "0";
      window.dispatchEvent(new Event("resize"));
      this.context.state.isFullscreen = true;
    }
  }

  exitFullscreen(): void {
    if (this.context.container) {
      const container = this.context.container;
      container.style.width = previousContainerStyle?.width!;
      container.style.height = previousContainerStyle?.height!;
      container.style.position = previousContainerStyle?.position!;
      container.style.zIndex = previousContainerStyle?.zIndex!;
      container.style.top = previousContainerStyle?.top!;
      container.style.left = previousContainerStyle?.left!;
      window.dispatchEvent(new Event("resize"));
      this.context.state.isFullscreen = false;
    }
  }

  setBearerToken(bearerToken: string) {
    this.context.bufferManager.setBearerToken(bearerToken);
  }

  addSubscriberToLoadingStatus(name: string, cb: (pctLoaded: number) => void) {
    this.app.addPctLoadedSubscribers(name, cb);
  }

  addSubscriberToContextChange(name: string, cb: (context: Context) => void) {
    this.app.addSubscribers(name, cb);
  }

  addCameraModeSubscriber(
    name: string,
    cb: (mode: PublicCameraConfig) => void
  ) {
    this.cameraModeSubscribers.set(name, cb);
  }

  init() {
    this.app.resetContext(this.context.options);
    this.app.init(this.context.container!);
  }

  cleanUp(isInternalReset: boolean = false) {
    this.app.teardown(isInternalReset);
  }
}
